import {
  fetchMobileAvailableOrderPackageList,
  fetchOrderPackage,
  fetchWUJIActivity,
  fetchWUJISupplier,
  querySubscribeFailReason,
} from 'lib/apis';
import { useMobileStore } from 'lib/store/mobileStore';
import UrlParams, { currentUrlParams } from 'lib/utils/url';
import { urlConfig } from 'lib/config';
import storage from 'lib/utils/storage';
import { captchaModalInit } from 'lib/container/SMSCaptchaModal';
import toast from 'lib/utils/toast';
import { showModal } from 'lib/components/Modal';
import { ORDER_CACHE_KEY } from 'lib/constants/common';
import { useGlobalStore } from 'lib/store';
import TipsModal from 'lib/components/Modal/TipsModal';
import TipsModalV2 from 'lib/components/Modal/TipsModalV2';
import { ERROR_CODE, ERROR_MSG, COMMON_MSG } from 'lib/constants/error';
import msdkUtil from 'lib/utils/MSDK';
import { getLoginType, getPackageName, getPureText } from 'lib/utils/util';
import { addReportPublicParams, report } from 'lib/utils/dataReport';
import { ATTA_EVENT_RESULT, ATTA_REPORT } from 'lib/constants/atta';
import monitor, { TRACE_ENUM } from 'lib/utils/monitor';
import { ENV_TYPE } from 'lib/constants/login';
import { generateYZFOrderIdKey } from 'lib/utils/storage/constants';
import { ORDER_STATE } from 'lib/constants/order';
import { SUPPLIER_MAP } from 'lib/constants/operator';
import { useGameCardGuideStore } from 'lib/store/gameCardGuideStore';
import channelService from './channelService';
import awardPrizeService from './awardPrizeService';
import gameCardGuideService from './gameCardGuideService';

const ONE_MINIUTE = 60 * 1000; // 60s

class MobileOrderPackageService {
  public get mobileStore() {
    return useMobileStore.getState();
  }

  public getCacheOrderKey(mobile) {
    return `${ORDER_CACHE_KEY}${mobile}`;
  }

  public queryMobileOrderStatus(mobile: string) {
    return this.mobileStore.queryMobileOrderStatus(mobile);
  }

  /**
   * 1、查询手机号可订购列表 | 查询供应商配置 | 查询活动配置
   *  a. 无可订购走安慰礼包逻辑
   *  b. 优先免费流量包isFree
   * 2、下单
   * 3、验证码
   */
  public async handleOrderFlowPackage(mobile, giftId) {
    toast.loading();
    const requestQueue = [
      fetchMobileAvailableOrderPackageList({ mobile, channelCode: channelService.channelCode }),
      fetchWUJIActivity(),
      fetchWUJISupplier(),
    ];
    const [packageListRes, activityRes, supplierRes] = await Promise.all(requestQueue);
    const supplierConfigList = supplierRes?.data || [];
    const activityConfigList = activityRes?.data || [];
    report(ATTA_REPORT.PACKAGE_LIST.RESULT, {
      mobile,
      eventResult: packageListRes?.code === 0 ? ATTA_EVENT_RESULT.SUCCESS : packageListRes?.code,
    });
    if (packageListRes?.code !== 0) {
      // 弹出服务器异常
      toast.close();
      TipsModalV2.show({
        content: ERROR_MSG[packageListRes?.code],
      });
      return;
    }
    const { packageList = [] } = packageListRes.data || {};
    let supplierId = packageList[0]?.supplierId;
    const supportPackageList = packageList.filter((c) => c.unsupportedReason === 0);

    // 如果存在可订购包，默认取第一个推荐
    if (supportPackageList.length) {
      supplierId = supportPackageList[0].supplierId;
    }

    const { operator_type: operatorType, supplier_phone: supplierPhone = '运营商客服' } =
      supplierConfigList.find((c) => c.supplier_id === supplierId) || {};
    addReportPublicParams({
      operatorType,
      // TODO 是所有后续都需要加 supplier 么？所有 supplier 都是 defaultSupplierId 么？需要后续确认
      supplier: parseInt(supplierId, 10),
      mobile,
    });

    if (!supportPackageList.length) {
      toast.close();
      const { unsupportedReason } = packageList[0] || {};
      // 暂时保持原有逻辑，这两个错误码不允许领取安慰礼包，大概率不会返回
      if ([201020, 201012].includes(unsupportedReason)) {
        TipsModal.show({
          content: `很抱歉，${
            ERROR_MSG[unsupportedReason] ? `由于${ERROR_MSG[unsupportedReason]}` : ''
          }您暂不支持参与此次活动，详讯${supplierPhone}`,
          confirmText: '确定',
        });
        return;
      }
      // 领取安慰礼包
      this.receiveNormalGift(giftId, unsupportedReason, supplierPhone);
      return;
    }

    // 优先0元包 默认取第一个推荐
    const { pId: packageId } = supportPackageList[0] || {};
    addReportPublicParams({
      product: packageId,
    });
    const {
      price_desc: packagePrice,
      package_name: packageName,
      is_promotional: isPromotional,
      subscribe_hint: orderTips,
    } = activityConfigList.find((c) => c.p_id === packageId) || {};
    this.orderFlowPackage({
      mobile,
      packageId,
      operatorType,
      supplierId,
      packagePrice,
      packageName: getPackageName(operatorType, isPromotional, packageName),
      orderTips: getPureText(orderTips),
      supplierPhone,
      isPromotional,
    });
  }

  private async handleYZFCacheOrder({
    mobile,
    packageId,
    operatorType,
    supplierId,
    packagePrice,
    packageName,
    orderTips,
    yzfOrderId,
    supplierPhone,
    isPromotional,
  }) {
    const res = await querySubscribeFailReason(yzfOrderId);
    toast.close();
    if (res?.code !== 0) {
      TipsModal.show({
        content: ERROR_MSG[res?.code] || COMMON_MSG,
      });
      return;
    }
    const orderState = res?.data?.orderState;
    const failReasonCode = Number(res?.data?.failReasonCode);

    switch (orderState) {
      case ORDER_STATE.CANCELING:
      case ORDER_STATE.CANCEL_SUCCESS:
      case ORDER_STATE.ORDER_SUCCESS:
        // 触发领奖
        location.reload();
        break;
      case ORDER_STATE.ORDERING:
        report(ATTA_REPORT.YZF.ORDERING_POP_VIEW);
        TipsModalV2.show({
          content: '订购申请已提交，请30分钟后查看结果、领取权益',
          confirmText: '确定',
        });
        break;
      case ORDER_STATE.ORDER_FAIL: {
        storage.removeStorage(generateYZFOrderIdKey(mobile));
        // 供应商错误
        if (ERROR_CODE.SUPPLIER_ERROR.includes(failReasonCode)) {
          report(ATTA_REPORT.YZF.ORDER_FAIL_POP_VIEW);
          TipsModal.show({
            content: `订购失败，如有疑问详讯${supplierPhone}`,
            confirmText: '确定',
          });
          break;
        }
        // 错误码相关错误
        if (ERROR_CODE.SMS_ERRORS.includes(failReasonCode)) {
          report(ATTA_REPORT.YZF.CODE_EXPIRED_POP_VIEW);
          TipsModal.show({
            content: '验证码已过期，请重新发起申请',
            confirmText: '确定',
            confirmCallback: () => {
              this.triggerSMSCode({
                supplierId,
                mobile,
                packageId,
                operatorType,
                priceDesc: packagePrice,
                packageName,
                orderTips,
                channelCode: channelService.channelCode,
                isPromotional,
              });
            },
          });
          break;
        }
        report(ATTA_REPORT.YZF.ORDER_FAIL_POP_VIEW);
        const errorMsg = ERROR_MSG[failReasonCode] || COMMON_MSG;
        TipsModal.show({
          content: errorMsg,
          confirmText: '确定',
        });
        break;
      }
      default:
        // 引导订购
        this.triggerSMSCode({
          mobile,
          packageId,
          operatorType,
          priceDesc: packagePrice,
          packageName,
          orderTips,
          channelCode: channelService.channelCode,
          supplierId,
          isPromotional,
        });
    }
  }

  // 下单逻辑
  private async orderFlowPackage({
    mobile,
    packageId,
    operatorType,
    supplierId,
    packagePrice,
    packageName,
    orderTips,
    supplierPhone,
    isPromotional,
  }) {
    const loginType = getLoginType();
    const yzfOrderId = this.getYZFOrderId(mobile, channelService.channelCode);
    // 翼支付的供应商ID在有登录态的情况下需要走特殊逻辑：判断订单状态
    if (supplierId === SUPPLIER_MAP.YZF && loginType !== ENV_TYPE.GUEST && !!yzfOrderId) {
      report(ATTA_REPORT.YZF.ORDER_CACHE_QUERY);
      this.handleYZFCacheOrder({
        mobile,
        packageId,
        operatorType,
        supplierId,
        packagePrice,
        packageName,
        orderTips,
        yzfOrderId,
        supplierPhone,
        isPromotional,
      });
      return;
    }
    if (this.isExistOrderCache(mobile, channelService.channelCode)) {
      toast.close();
      this.triggerSMSCode({
        mobile,
        packageId,
        operatorType,
        priceDesc: packagePrice,
        packageName,
        orderTips,
        channelCode: channelService.channelCode,
        supplierId,
        isPromotional,
      });
      return;
    }
    const subStatTag = currentUrlParams.getCurrentParams('SubStatTag') || channelService.channelTag;
    const redirectURL = this.generatorOrderUrl({
      ...currentUrlParams.getCurrentParams(),
      businessId: packageId,
      app: channelService.gameCode,
      action: 1,
      Channel: channelService.channelCode,
      operator: operatorType,
      supplierId,
      // orderState: userOrderInfo.orderState, // 这里下单的时候 传过去没啥意义
      SubStatTag: subStatTag,
      orderSrc: 'activity',
    });

    monitor.reportLog({
      actionName: TRACE_ENUM.ORDER,
      pId: packageId,
    });
    monitor.reportEvent(TRACE_ENUM.ORDER);

    const res = await fetchOrderPackage({
      packageId,
      channelCode: channelService.channelCode,
      mobile,
      subStatTag,
      redirectURL,
      shareToken: currentUrlParams.getCurrentParams('shareToken'),
    });
    toast.close();
    report(ATTA_REPORT.ORDER_PACKAGE.RESULT, {
      eventResult: res?.code !== 0 ? ATTA_EVENT_RESULT.SUCCESS : res?.code,
    });
    if (res?.code !== 0) {
      TipsModalV2.show({
        content: ERROR_MSG[res?.code],
      });
      return;
    }
    monitor.reportEvent(TRACE_ENUM.SMSCODE);

    const { url, orderMethod } = res?.data || {};

    if (url && orderMethod === 1) {
      monitor.reportLog({
        actionName: TRACE_ENUM.UNICOM_ORDER,
      });
      window.location.href = url;
      return;
    }

    storage.setStorage(
      this.getCacheOrderKey(mobile),
      {
        pId: packageId,
        channelCode: channelService.channelCode,
        mobile,
        subStatTag,
        redirectURL,
      },
      ONE_MINIUTE + Date.now(),
    );

    // 触发验证码
    this.triggerSMSCode({
      mobile,
      packageId,
      operatorType,
      priceDesc: packagePrice,
      packageName,
      orderTips,
      channelCode: channelService.channelCode,
      supplierId,
      isPromotional,
    });
  }

  // 弹出验证码弹窗
  private triggerSMSCode(params) {
    const globalStore = useGlobalStore.getState();
    const { smsModalTheme } = globalStore;
    showModal(captchaModalInit({ ...params, smsModalTheme }), {
      onBottom: !!smsModalTheme,
    });
  }

  private isExistOrderCache(mobile, channelCode) {
    const cacheOrderInfo = storage.getStorage(this.getCacheOrderKey(mobile)) || {};
    if (!cacheOrderInfo.channelCode) return false;
    if (cacheOrderInfo?.channelCode !== channelCode) return false;
    return true;
  }

  // 检查翼支付订单缓存
  private getYZFOrderId(mobile, channelCode) {
    const cacheOrderInfo = storage.getPureStorage(generateYZFOrderIdKey(mobile))?.value || {};
    if (!cacheOrderInfo?.channelCode || cacheOrderInfo?.channelCode !== channelCode) return undefined;
    return cacheOrderInfo.orderId;
  }

  private generatorOrderUrl(params) {
    return UrlParams.appendUrlParams(params, `${urlConfig.orderPageUrl}flow/result`);
  }

  // 领取安慰礼包
  private receiveNormalGift(giftId, unsupportedReason, supplierPhone) {
    const errMsg = unsupportedReason && ERROR_MSG[unsupportedReason] ? `由于${ERROR_MSG[unsupportedReason]}，` : '';
    const content =
      giftId && msdkUtil.isMSDK
        ? `很抱歉，${errMsg}您的手机号码不支持参与此次活动，送您【普通礼包】`
        : `很抱歉，${errMsg}您暂不支持参与此次活动，详讯${supplierPhone}`;
    useGameCardGuideStore.getState().setUnsupportedReason(unsupportedReason);
    if (giftId && msdkUtil.isMSDK) {
      const footer = gameCardGuideService.buildGuideFooter('giftmodal', { unsupportedReason });
      report(ATTA_REPORT.UNSUPPORTED_MODAL.GIFT_MODAL_SHOW);
      TipsModalV2.show({
        content,
        confirmText: '去领取',
        confirmCallback: () => {
          if (awardPrizeService.getGiftReceiveStatus(giftId)) {
            toast.info('礼包已被领取');
            return;
          }
          awardPrizeService.receiveGift(giftId);
        },
        showClose: !!footer,
        footer,
      });
      return;
    }

    const noGiftGuide = gameCardGuideService.getNoGiftGuideConfig('nogiftmodal', { unsupportedReason });
    report(ATTA_REPORT.UNSUPPORTED_MODAL.NOGIFT_MODAL_SHOW);
    TipsModalV2.show({
      content,
      confirmText: noGiftGuide?.confirmText,
      confirmCallback: noGiftGuide?.confirmCallback,
      confirmStyle: noGiftGuide?.confirmStyle,
      showClose: !!noGiftGuide,
      footer: noGiftGuide?.footer,
    });
  }
}

const mobileOrderPackageService = new MobileOrderPackageService();
export default mobileOrderPackageService;
